import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPricingJob, PricingJobDetail } from '../api/pricing';

const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'expired',
  'committed',
  'discarded',
]);

interface UsePricingJobOptions {
  pollIntervalMs?: number;
  enabled?: boolean;
}

export function usePricingJob(jobId: string | null, options: UsePricingJobOptions = {}) {
  const { pollIntervalMs = 4000, enabled = true } = options;
  const [job, setJob] = useState<PricingJobDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const shouldPoll = useMemo(() => Boolean(jobId) && enabled, [jobId, enabled]);

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await getPricingJob(jobId);
      setJob(result);
      if (TERMINAL_STATUSES.has(result.status)) {
        setIsPolling(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing job');
      setIsPolling(false);
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  // Initial fetch
  useEffect(() => {
    if (shouldPoll) {
      fetchJob();
      setIsPolling(true);
    } else {
      setIsPolling(false);
      setJob(null);
    }
  }, [shouldPoll, fetchJob]);

  // Polling loop
  useEffect(() => {
    if (!shouldPoll || !isPolling) return;

    const id = setInterval(() => {
      fetchJob();
    }, pollIntervalMs);

    return () => clearInterval(id);
  }, [shouldPoll, isPolling, pollIntervalMs, fetchJob]);

  const startPolling = useCallback(() => {
    if (shouldPoll) {
      setIsPolling(true);
    }
  }, [shouldPoll]);

  const stopPolling = useCallback(() => setIsPolling(false), []);

  return {
    job,
    isLoading,
    error,
    isPolling,
    refetch: fetchJob,
    startPolling,
    stopPolling,
  };
}
